/**
 * Accept Invitation Page
 *
 * Allows invited users to set their password and activate their account.
 * Extracts invitation token from URL query parameter and validates it.
 */

import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RootState } from '../types';
import { ApiBase } from '../apis/ApiBase';
import { authClient } from '../apis/client-instances';
import Logo from '../assets/images/logo.webp';
import { CheckCircleIcon, LockOutlinedIcon, VisibilityIcon, VisibilityOffIcon } from '../components/icons';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  OutlinedInput,
  Paper,
  Typography,
} from '../components/ui';
import { setUser } from '../features/profile/profileSlice';
import { alert } from '../hooks/function';

export default function AcceptInvitePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();

  // Get account data from Redux (for tenant branding)
  const accountName = useSelector((state: RootState) => state.account.name);
  const accountLogo = useSelector((state: RootState) => state.account.logoUrl);

  const [token, setToken] = useState<string>('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<any>({});

  // Set page title with account name
  useEffect(() => {
    if (accountName) {
      document.title = `${accountName} - Accept Invitation`;
    }
  }, [accountName]);

  // Extract token from URL on mount
  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      alert(enqueueSnackbar, 'Invalid invitation link. Please check your email for the correct link.', 'error');
      navigate('/signin');
    } else {
      setToken(tokenParam);
    }
  }, [searchParams, navigate, enqueueSnackbar]);

  const validate = () => {
    const newErrors: any = {};

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validate()) return;

    try {
      setIsLoading(true);
      const result = await authClient.acceptInvite({
        token,
        password,
        confirmPassword,
      });

      // Store token and user data (auto-login after accepting invitation)
      if (result.token) {
        localStorage.setItem('token', result.token);
      }
      dispatch(setUser(result));

      alert(enqueueSnackbar, 'Welcome! Your account has been activated successfully.', 'success');
      navigate('/course'); // Redirect to courses dashboard
    } catch (error: any) {
      alert(
        enqueueSnackbar,
        ApiBase.getCleanErrorMessage(error, 'Failed to accept invitation. The link may be invalid or expired.'),
        'error'
      );
      setIsLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Paper
        elevation={3}
        sx={{
          marginTop: 8,
          padding: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box sx={{ mb: 2, textAlign: 'center' }}>
          <Box
            component="img"
            src={accountLogo || Logo}
            alt={accountName || 'LMS'}
            sx={{
              maxHeight: 60,
              maxWidth: '100%',
              height: 'auto',
              objectFit: 'contain',
              mb: 2,
              display: 'inline-block',
            }}
          />
        </Box>

        <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
          <CheckCircleIcon />
        </Avatar>

        <Typography component="h1" variant="h5" sx={{ mb: 1 }}>
          Accept Invitation
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
          Set your password to activate your account
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
          <FormControl fullWidth variant="outlined" margin="normal" error={!!errors.password}>
            <InputLabel htmlFor="password">Password *</InputLabel>
            <OutlinedInput
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setErrors({ ...errors, password: '' });
              }}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              }
              label="Password"
              autoFocus
            />
            {errors.password && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                {errors.password}
              </Typography>
            )}
          </FormControl>

          <FormControl fullWidth variant="outlined" margin="normal" error={!!errors.confirmPassword}>
            <InputLabel htmlFor="confirmPassword">Confirm Password *</InputLabel>
            <OutlinedInput
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setErrors({ ...errors, confirmPassword: '' });
              }}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              }
              label="Confirm Password"
            />
            {errors.confirmPassword && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                {errors.confirmPassword}
              </Typography>
            )}
          </FormControl>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 2 }}>
            Password must be at least 8 characters long
          </Typography>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={isLoading}
            sx={{ mt: 2, mb: 2, fontSize: 15, borderRadius: 2, height: 50 }}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Activate Account'}
          </Button>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Already have an account?{' '}
              <Link
                onClick={() => navigate('/signin')}
                sx={{
                  color: 'primary.main',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                Sign In
              </Link>
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}
